import { Host, h } from "@stencil/core";
import { paginationGoToFirstPage, paginationGoToLastPage, paginationGoToNextPage, paginationGoToPage, paginationGoToPreviousPage } from "./gx-grid-chameleon-paginator";
import { gridRefresh, gridSort } from "./gx-grid-chameleon-actions";
import { GridChameleonManagerState } from "./gx-grid-chameleon-state";
// eslint-disable-next-line @stencil-community/required-prefix
export class GridChameleon {
    constructor() {
        this.grid = undefined;
        this.gridTimestamp = undefined;
        this.state = undefined;
    }
    controlStateHandler() {
        this.loadState();
    }
    componentWillLoad() {
        this.loadState();
    }
    componentWillRender() {
        this.defineColumnRender();
    }
    componentDidLoad() {
        this.setCurrentRow();
        this.notifyResizePopup();
    }
    selectionChangedHandler(eventInfo) {
        const rowIndex = this.getRowIndexByGxId(eventInfo.detail.rowsId[0]);
        if (rowIndex >= 0) {
            this.grid.execC2VFunctions();
            this.grid.selectRow(rowIndex);
        }
    }
    cellClickedHandler(eventInfo) {
        const rowIndex = this.getRowIndexByGxId(eventInfo.detail.rowId);
        const cellIndex = parseInt(eventInfo.detail.cellId);
        if (rowIndex >= 0) {
            this.grid.executeEvent(cellIndex, rowIndex);
        }
    }
    navigateClickedHandler(eventInfo) {
        switch (eventInfo.detail.type) {
            case "first":
                paginationGoToFirstPage(this.grid);
                break;
            case "previous":
                paginationGoToPreviousPage(this.grid);
                break;
            case "next":
                paginationGoToNextPage(this.grid);
                break;
            case "last":
                paginationGoToLastPage(this.grid);
                break;
            case "goto":
                paginationGoToPage(this.grid, eventInfo.detail.page);
                break;
        }
    }
    refreshClickedHandler() {
        gridRefresh(this.grid);
    }
    columnHiddenChangedHandler(eventInfo) {
        GridChameleonManagerState.setColumnHidden(eventInfo.detail.columnId, eventInfo.detail.hidden);
    }
    columnSizeChangedHandler(eventInfo) {
        GridChameleonManagerState.setColumnSize(eventInfo.detail.columnId, eventInfo.detail.size);
    }
    columnFreezeChangedHandler(eventInfo) {
        GridChameleonManagerState.setColumnFreeze(eventInfo.detail.columnId, eventInfo.detail.freeze);
    }
    columnSortChangedHandler(eventInfo) {
        GridChameleonManagerState.setColumnSort(eventInfo.detail.columnId, eventInfo.detail.sortDirection);
        switch (this.grid.SortMode) {
            case "client":
                gridSort(this.grid, eventInfo.detail.columnId, eventInfo.detail.sortDirection);
                this.gridTimestamp = Date.now();
                break;
            case "server":
                gridRefresh(this.grid);
                break;
        }
    }
    columnOrderChangedHandler(eventInfo) {
        GridChameleonManagerState.setColumnOrder(eventInfo.detail.columnId, eventInfo.detail.order);
    }
    columnFilterChangedHandler(eventInfo) {
        const column = eventInfo.detail.column;
        column.filterEqual = eventInfo.detail.equal ?? "";
        column.filterLess = eventInfo.detail.less ?? "";
        column.filterGreater = eventInfo.detail.greater ?? "";
        GridChameleonManagerState.setColumnFilterEqual(column.htmlName, column.filterEqual);
        GridChameleonManagerState.setColumnFilterLess(column.htmlName, column.filterLess);
        GridChameleonManagerState.setColumnFilterGreater(column.htmlName, column.filterGreater);
        gridRefresh(this.grid);
    }
    getColumnSize(column) {
        let size;
        switch (column.Size) {
            case "auto":
                size = "auto";
                break;
            case "css":
                size = `var(--${column.SizeVariableName}, min-content)`;
                break;
            case "length":
                size = column.SizeLength;
                break;
            case "max":
                size = "max-content";
                break;
            case "min":
                size = "min-content";
                break;
            case "minmax":
                size = `minmax(${column.SizeMinLength || "min-content"}, ${column.SizeMaxLength || "auto"})`;
                break;
            default:
                size = "min-content";
                break;
        }
        return size || "min-content";
    }
    defineColumnRender() {
        const properties = this.grid.properties;
        this.grid.columns.forEach((column, i) => {
            column.render =
                (properties.length === 0 && column.visible) ||
                    properties.some(row => row[i].visible);
        });
    }
    getRowIndexByGxId(rowId) {
        return this.grid.rows.findIndex(row => row.gxId === rowId);
    }
    notifyResizePopup() {
        if (gx.popup.ispopup()) {
            gx.fx.obs.notify("gx.onafterevent");
        }
    }
    setCurrentRow() {
        const firstRow = this.grid.rows[0];
        if (firstRow && !gx.fn.currentGridRowImpl(this.grid.gxId)) {
            gx.fn.setCurrentGridRow(this.grid.gxId, firstRow.gxId);
        }
    }
    loadState() {
        GridChameleonManagerState.load(this.grid, this.state);
    }
    renderTitle() {
        return h("h1", { slot: "header" }, this.grid.header);
    }
    renderActionbar(position, className) {
        const refresh = this.grid.ActionRefreshPosition === position, settings = this.grid.ActionSettingsPosition === position;
        if (refresh || settings) {
            return (h("ch-grid-actionbar", { slot: position, class: className }, refresh && this.renderActionRefresh(), settings && this.renderActionSettings()));
        }
    }
    renderActionRefresh() {
        return (h("ch-grid-action-refresh", { class: this.grid.ActionRefreshClass, title: this.grid.ActionRefreshTextPosition === "title"
                ? gx.getMessage("GX_BtnRefresh")
                : "" }, this.grid.ActionRefreshTextPosition === "text"
            ? gx.getMessage("GX_BtnRefresh")
            : ""));
    }
    renderActionSettings() {
        return (h("ch-grid-action-settings", { class: this.grid.ActionSettingsClass, title: this.grid.ActionSettingsTextPosition === "title"
                ? gx.getMessage("GXM_Settings")
                : "" }, this.grid.ActionSettingsTextPosition === "text"
            ? gx.getMessage("GXM_Settings")
            : ""));
    }
    renderColumns() {
        return (h("ch-grid-columnset", { class: this.grid.ColumnsetClass }, this.grid.columns.map(column => {
            if (gx.lang.gxBoolean(column.render)) {
                return (h("ch-grid-column", { key: column.htmlName, columnId: column.htmlName, columnIconUrl: column.Icon, columnName: column.title, columnNamePosition: column.NamePosition, size: this.getColumnSize(column), order: column.order ? column.order : null, displayObserverClass: column.gxColumnClass, class: `${this.grid.ColumnClass} ${column.HeaderClass} ${column.isFiltering ? "grid-column-filtering" : ""}`, hidden: column.Hidden === -1, hideable: column.Hideable === -1, resizable: column.Resizeable === -1, sortable: column.Sortable === -1, settingable: column.Filterable === -1, sortDirection: column.SortDirection }, column.Filterable === -1 && this.renderColumnFilter(column)));
            }
        })));
    }
    renderColumnFilter(column) {
        return (h("gx-grid-chameleon-column-filter", { key: column.htmlName, class: this.grid.ColumnFilterClass, column: column, equal: column.filterEqual, less: column.filterLess, greater: column.filterGreater, buttonResetText: this.grid.FilterButtonResetText, buttonApplyText: this.grid.FilterButtonApplyText, slot: "settings" }));
    }
    renderRows() {
        return this.grid.rows.map((row, i) => {
            const rowEvenClasses = `${this.grid.RowClass} ${this.grid.RowEvenClass}`;
            const rowOddClasses = `${this.grid.RowClass} ${this.grid.RowOddClass}`;
            return (h("ch-grid-row", { key: row.gxId, rowid: row.gxId, class: i % 2 === 0 ? rowEvenClasses : rowOddClasses }, this.renderCells(row)));
        });
    }
    renderCells(row) {
        return row.gxProps.map((cellControlProperties, i) => {
            const column = this.grid.columns[i];
            if (gx.lang.gxBoolean(column.render)) {
                return (h("ch-grid-cell", { key: cellControlProperties[0], class: this.grid.CellClass, cellid: i, innerHTML: this.renderControl(column.gxControl, cellControlProperties) }));
            }
        });
    }
    renderControl(control, props) {
        // eslint-disable-next-line prefer-spread
        control.setProperties.apply(control, props);
        return control.getHtml();
    }
    renderPaginator() {
        const hasRecordCount = !!this.grid.ParentObject[`sub${this.grid.ControlName}_Recordcount`];
        const recordCount = this.grid.ParentObject[`sub${this.grid.ControlName}_Recordcount`] ?? 0;
        const hasNextPage = !this.grid.isLastPage();
        const activePage = Math.ceil((parseInt(this.grid.firstRecordOnPage) + 1) / this.grid.pageSize);
        const totalPages = Math.max(1, Math.ceil(recordCount / this.grid.pageSize));
        return (h("ch-paginator", { "has-next-page": hasRecordCount ? null : hasNextPage, "active-page": activePage, "total-pages": hasRecordCount ? totalPages : -1, class: this.grid.pagingBarClass, slot: "footer" }, this.renderPaginatorNavigate("first", this.grid.pagingButtonFirstClass, gx.getMessage("GXM_first")), this.renderPaginatorNavigate("previous", this.grid.pagingButtonPreviousClass, gx.getMessage("GXM_previous")), this.grid.PaginatorPagesShow && (h("ch-paginator-pages", { maxSize: this.grid.PaginatorPagesMaxSize })), this.renderPaginatorNavigate("next", this.grid.pagingButtonNextClass, gx.getMessage("GXM_next")), this.renderPaginatorNavigate("last", this.grid.pagingButtonLastClass, gx.getMessage("GXM_last"))));
    }
    renderPaginatorNavigate(type, className, text) {
        const textPosition = this.grid.PaginatorNavigationButtonTextPosition;
        return (h("ch-paginator-navigate", { type: type, class: className, title: textPosition === "title" ? text : "" }, textPosition === "text" ? text : ""));
    }
    renderPaginationInfo() {
        const recordCount = this.grid.ParentObject[`sub${this.grid.ControlName}_Recordcount`] ?? 0;
        let mask = this.grid.PaginatorInfoEmptyTextMask;
        let recordStart = 0;
        let recordEnd = 0;
        if (recordCount > 0) {
            mask = this.grid.PaginatorInfoTextMask;
            recordStart = parseInt(this.grid.firstRecordOnPage) + 1;
            recordEnd = Math.min(recordStart + this.grid.pageSize - 1, recordCount);
        }
        const replacements = {
            "record-count": recordCount,
            "record-start": recordStart,
            "record-end": recordEnd
        };
        const text = mask.replace(/\{([^}]+)\}/g, (_match, capture) => {
            return replacements[capture] ?? capture;
        });
        return (h("div", { class: this.grid.PaginatorInfoClass, slot: "footer" }, text));
    }
    render() {
        return (h(Host, { key: '74f29a6cb68733c9fcf3361a82616d1d01539a32' }, h("ch-grid", { key: '8c7add357fc155c890d61632e0d029fbc5aedeff', class: this.grid.Class, rowSelectionMode: this.grid.gxAllowSelection ? "single" : "none", rowSelectedClass: this.grid.RowSelectedClass.trim(), rowHighlightedClass: this.grid.RowHighlightedClass.trim() }, this.grid.header && this.renderTitle(), this.renderActionbar("header", this.grid.ActionbarHeaderClass), this.renderActionbar("footer", this.grid.ActionbarFooterClass), this.renderColumns(), this.renderRows(), this.grid.PaginatorShow &&
            this.grid.PaginatorInfoShow &&
            this.grid.pageSize > 0 &&
            this.renderPaginationInfo(), this.grid.PaginatorShow &&
            this.grid.pageSize > 0 &&
            this.renderPaginator())));
    }
    static get is() { return "gx-grid-chameleon"; }
    static get originalStyleUrls() {
        return {
            "$": ["gx-grid-chameleon.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["gx-grid-chameleon.css"]
        };
    }
    static get properties() {
        return {
            "grid": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "GxGrid",
                    "resolved": "GxGrid",
                    "references": {
                        "GxGrid": {
                            "location": "import",
                            "path": "./genexus",
                            "id": "src/components/gx-grid/genexus.ts::GxGrid"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The GxGrid instance representing the data to be displayed in the grid."
                }
            },
            "gridTimestamp": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The timestamp indicating the time when the grid was last updated."
                },
                "attribute": "grid-timestamp",
                "reflect": false
            },
            "state": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "GridChameleonState",
                    "resolved": "GridChameleonState",
                    "references": {
                        "GridChameleonState": {
                            "location": "import",
                            "path": "./gx-grid-chameleon-state",
                            "id": "src/components/gx-grid/gx-grid-chameleon-state.ts::GridChameleonState"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The UI state of the Grid."
                }
            }
        };
    }
    static get watchers() {
        return [{
                "propName": "state",
                "methodName": "controlStateHandler"
            }];
    }
    static get listeners() {
        return [{
                "name": "selectionChanged",
                "method": "selectionChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "rowClicked",
                "method": "cellClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "pageNavigationRequested",
                "method": "navigateClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "refreshClicked",
                "method": "refreshClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnHiddenChanged",
                "method": "columnHiddenChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnSizeChanging",
                "method": "columnSizeChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnSizeChanged",
                "method": "columnSizeChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnFreezeChanged",
                "method": "columnFreezeChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnSortChanged",
                "method": "columnSortChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnOrderChanged",
                "method": "columnOrderChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnSettingsChanged",
                "method": "columnFilterChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
